// src/components/ScreenLockerWeb.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  checkPasscodeApi,
  verifyPasscodeApi,
  setPasscodeApi,
  lockScreenApi,
  setFinanceLockEnabledApi,
} from "../services/passcodeApi";

type ScreenLockerWebProps = {
  storageKey?: string; // default: "finance"
  children:
    | React.ReactNode
    | ((lockControl: React.ReactNode) => React.ReactNode);
  autoLockMs?: number; // default: 0 (off)
};

const ENABLED_KEY = (k: string) => `lock:${k}:enabled`;
const UNLOCKED_KEY = (k: string) => `lock:${k}:unlocked`; // session-only

function getLS(key: string) {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}
function setLS(key: string, val: string) {
  try {
    localStorage.setItem(key, val);
  } catch {}
}
function rmLS(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {}
}
function getSS(key: string) {
  try {
    return sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
}
function setSS(key: string, val: string) {
  try {
    sessionStorage.setItem(key, val);
  } catch {}
}
function rmSS(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch {}
}

export default function ScreenLockerWeb({
  storageKey = "finance",
  children,
  autoLockMs = 0,
}: ScreenLockerWebProps) {
  const hasStoredPreference = getLS(ENABLED_KEY(storageKey)) === "1";
  const [enabled, setEnabled] = useState(hasStoredPreference);
  const [unlocked, setUnlocked] = useState(
    getSS(UNLOCKED_KEY(storageKey)) === "1",
  );
  const [preferenceResolved, setPreferenceResolved] =
    useState(hasStoredPreference);

  const [serverHasPin, setServerHasPin] = useState<boolean | null>(null);

  const [showPanel, setShowPanel] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinStep, setPinStep] = useState<
    "enter" | "set" | "confirm" | "password"
  >("enter");
  const [tempPin, setTempPin] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [panelError, setPanelError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pendingEnableAfterPin, setPendingEnableAfterPin] = useState(false);
  const [unlockPermanently, setUnlockPermanently] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const pwdRef = useRef<HTMLInputElement>(null);
  const idleTimer = useRef<number | null>(null);

  // If lock is enabled, always gate entry first on screen visit.
  useEffect(() => {
    if (getLS(ENABLED_KEY(storageKey)) === "1") {
      rmSS(UNLOCKED_KEY(storageKey));
      setUnlocked(false);
    }
  }, [storageKey]);

  // Focus field on modal open
  useEffect(() => {
    if (!showPanel) return;
    const t = setTimeout(() => {
      if (pinStep === "password") pwdRef.current?.focus();
      else inputRef.current?.focus();
    }, 60);
    return () => clearTimeout(t);
  }, [showPanel, pinStep]);

  useEffect(() => {
    setPanelError("");
  }, [pinStep, showPanel]);

  // Sync local state from backend. If there is no local flag yet, treat the
  // server as the source of truth so lock preference follows the user.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { exists, enabled: backendEnabled } = await checkPasscodeApi();
        if (!mounted) return;
        setServerHasPin(!!exists);
        if (backendEnabled) {
          setLS(ENABLED_KEY(storageKey), "1");
          rmSS(UNLOCKED_KEY(storageKey));
          setEnabled(true);
          setUnlocked(false);
        } else {
          rmLS(ENABLED_KEY(storageKey));
          rmSS(UNLOCKED_KEY(storageKey));
          setEnabled(false);
          setUnlocked(true);
        }
      } catch {
        if (mounted) {
          setServerHasPin(false);
          if (!hasStoredPreference) {
            setEnabled(false);
            setUnlocked(true);
          }
        }
      } finally {
        if (mounted) setPreferenceResolved(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [hasStoredPreference, storageKey]);

  const lockNow = useCallback(async () => {
    rmSS(UNLOCKED_KEY(storageKey));
    setUnlocked(false);
    try {
      await lockScreenApi();
    } catch {}
    if (enabled) {
      setPinStep(serverHasPin === false ? "set" : "enter");
      setShowPanel(true);
      setPanelError("");
      setPinInput("");
      setTempPin("");
      setAccountPassword("");
    }
  }, [enabled, serverHasPin, storageKey]);

  const toggleEnabled = useCallback(async () => {
    if (!enabled) {
      try {
        setSubmitting(true);
        let nextHasPin = serverHasPin;
        if (nextHasPin === null) {
          const { exists } = await checkPasscodeApi();
          nextHasPin = !!exists;
          setServerHasPin(nextHasPin);
        }
        rmSS(UNLOCKED_KEY(storageKey));
        setShowPanel(true);
        setPinInput("");
        setTempPin("");
        setAccountPassword("");
        if (nextHasPin === false) {
          setPendingEnableAfterPin(true);
          setPinStep("set");
        } else {
          await setFinanceLockEnabledApi(true);
          setEnabled(true);
          setLS(ENABLED_KEY(storageKey), "1");
          setUnlocked(false);
          setPendingEnableAfterPin(false);
          setPinStep("enter");
        }
      } catch {
        window.alert(
          "Could not enable Finance Lock right now. Please try again.",
        );
      } finally {
        setSubmitting(false);
      }
    } else {
      try {
        setSubmitting(true);
        await setFinanceLockEnabledApi(false);
        setEnabled(false);
        rmLS(ENABLED_KEY(storageKey));
        rmSS(UNLOCKED_KEY(storageKey));
        setUnlocked(true);
        setShowPanel(false);
        setPinInput("");
        setTempPin("");
        setAccountPassword("");
        await lockScreenApi();
      } catch {
        window.alert(
          "Could not disable Finance Lock right now. Please try again.",
        );
      } finally {
        setSubmitting(false);
      }
    }
  }, [enabled, serverHasPin, storageKey]);

  // Idle auto-lock
  useEffect(() => {
    if (!enabled || !unlocked || !autoLockMs) return;
    const resetTimer = () => {
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
      idleTimer.current = window.setTimeout(() => {
        lockNow();
      }, autoLockMs);
    };
    const events = [
      "mousemove",
      "keydown",
      "scroll",
      "click",
      "touchstart",
      "visibilitychange",
    ];
    events.forEach((e) =>
      window.addEventListener(e, resetTimer, { passive: true }),
    );
    resetTimer();
    return () => {
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
  }, [enabled, unlocked, autoLockMs, lockNow]);

  // When locked, jump straight to PIN modal (no intermediate card).
  useEffect(() => {
    if (!enabled || unlocked || showPanel) return;
    setPinStep(serverHasPin === false ? "set" : "enter");
    setPanelError("");
    setPinInput("");
    setTempPin("");
    setAccountPassword("");
    setUnlockPermanently(false);
    setShowPanel(true);
  }, [enabled, unlocked, showPanel, serverHasPin]);

  const shake = () => {
    inputRef.current?.classList.add("animate-shake");
    setTimeout(() => inputRef.current?.classList.remove("animate-shake"), 300);
  };

  const unlockWithPin = useCallback(
    async (pin: string) => {
      try {
        setSubmitting(true);
        setPanelError("");
        await verifyPasscodeApi(pin);
        if (unlockPermanently) {
          await setFinanceLockEnabledApi(false);
          setEnabled(false);
          rmLS(ENABLED_KEY(storageKey));
          rmSS(UNLOCKED_KEY(storageKey));
          setPendingEnableAfterPin(false);
          try {
            await lockScreenApi();
          } catch {}
        } else {
          setSS(UNLOCKED_KEY(storageKey), "1");
        }
        setUnlocked(true);
        setShowPanel(false);
        setPinInput("");
        setUnlockPermanently(false);
      } catch {
        setPinInput("");
        setPanelError(
          unlockPermanently
            ? "Could not disable lock. Check your PIN and try again."
            : "Incorrect PIN. Please try again.",
        );
        shake();
      } finally {
        setSubmitting(false);
      }
    },
    [storageKey, unlockPermanently],
  );

  const handlePinInputChange = (value: string) => {
    const nextPin = value.replace(/\D/g, "").slice(0, 6);
    setPinInput(nextPin);

    if (pinStep === "enter" && nextPin.length === 4 && !submitting) {
      void unlockWithPin(nextPin);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    if (pinStep === "password") {
      if (!accountPassword || !/^\d{4,6}$/.test(tempPin)) {
        setPanelError("Please enter your account password.");
        return;
      }
      try {
        setSubmitting(true);
        setPanelError("");
        await setPasscodeApi(accountPassword, tempPin);
        setServerHasPin(true);
        if (pendingEnableAfterPin) {
          await setFinanceLockEnabledApi(true);
          setEnabled(true);
          setLS(ENABLED_KEY(storageKey), "1");
          rmSS(UNLOCKED_KEY(storageKey));
          setUnlocked(false);
          setPendingEnableAfterPin(false);
        }
        setPinStep("enter");
        setPinInput("");
        setTempPin("");
        setAccountPassword("");
      } catch (err: any) {
        setPanelError(err?.message || "Failed to save PIN. Try again.");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    const v = pinInput.trim();
    if (!/^\d{4,6}$/.test(v)) {
      setPanelError("PIN must be 4 to 6 digits.");
      return;
    }

    if (pinStep === "enter") {
      await unlockWithPin(v);
    } else if (pinStep === "set") {
      setTempPin(v);
      setPinInput("");
      setPinStep("confirm");
    } else {
      // confirm
      if (v === tempPin) {
        setPinInput("");
        setPinStep("password");
        setTimeout(() => pwdRef.current?.focus(), 50);
      } else {
        setPanelError("PINs do not match. Please re-enter.");
        setTempPin("");
        setPinInput("");
        setPinStep("set");
      }
    }
  };

  const headerBtn = useMemo(
    () => (
      <button
        type="button"
        onClick={() => (enabled && unlocked ? lockNow() : toggleEnabled())}
        className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs shadow-sm transition-colors ${
          enabled
            ? unlocked
              ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              : "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
        }`}
        title={
          enabled
            ? unlocked
              ? "Lock"
              : "Unlock permanently (disable lock)"
            : "Enable Lock"
        }
      >
        {enabled ? (
          unlocked ? (
            <>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="text-slate-700"
              >
                <path d="M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
                <path d="M17 8V7a5 5 0 0 0-10 0v1H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-2Zm-8 0V7a3 3 0 0 1 6 0v1H9Z" />
              </svg>
              Lock
            </>
          ) : (
            <>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="text-slate-700"
              >
                <path d="M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
                <path d="M17 8V7a5 5 0 0 0-10 0v1H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-2Zm-8 0V7a3 3 0 0 1 6 0v1H9Z" />
              </svg>
              Unlock Permanently
            </>
          )
        ) : (
          <>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="text-slate-700"
            >
              <path d="M18 8h-1V6a5 5 0 1 0-10 0v2H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2ZM8 6a4 4 0 1 1 8 0v2H8V6Z" />
            </svg>
            Enable Lock
          </>
        )}
      </button>
    ),
    [enabled, unlocked, lockNow, toggleEnabled],
  );

  const shouldShowContent = preferenceResolved && (!enabled || unlocked); // don't render sensitive DOM when locked
  const contentNode =
    typeof children === "function" ? children(headerBtn) : children;
  const shouldRenderFloatingButton =
    typeof children !== "function" && preferenceResolved;
  const shouldShowPreferenceSkeleton = !preferenceResolved && !showPanel;

  return (
    <div className="relative">
      {/* top-right control */}
      {shouldRenderFloatingButton && (
        <div className="absolute right-0 -top-10 md:top-0 md:-mt-10">
          {headerBtn}
        </div>
      )}

      {/* content */}
      {shouldShowContent ? (
        <div className="transition-all duration-300">{contentNode}</div>
      ) : shouldShowPreferenceSkeleton ? (
        <div className="mx-auto max-w-6xl space-y-4 px-4 md:px-6 py-4 animate-pulse">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="h-7 w-44 rounded-md bg-slate-200" />
              <div className="h-4 w-32 rounded-md bg-slate-100" />
            </div>
            <div className="h-9 w-44 rounded-md bg-slate-200" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-[78px] rounded-md border border-slate-200 bg-white p-3"
              >
                <div className="h-full w-full rounded-md bg-slate-100" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <div className="h-[290px] rounded-md border border-slate-200 bg-white p-3">
              <div className="h-full w-full rounded-md bg-slate-100" />
            </div>
            <div className="h-[290px] rounded-md border border-slate-200 bg-white p-3">
              <div className="h-full w-full rounded-md bg-slate-100" />
            </div>
          </div>
        </div>
      ) : (
        <div className="h-72" />
      )}

      {/* Modal */}
      {showPanel && (
        <div className="fixed inset-x-0 bottom-0 top-16 z-40 flex items-center justify-center bg-black/50 backdrop-blur-md p-4">
          <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-start gap-3">
              <div className="mt-0.5 rounded-md bg-slate-100 p-2 text-slate-700">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
                  <path d="M17 8V7a5 5 0 0 0-10 0v1H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-2Zm-8 0V7a3 3 0 0 1 6 0v1H9Z" />
                </svg>
              </div>
              <div className="min-w-0">
                <div className="text-base font-semibold text-slate-900">
                  {pinStep === "enter" && "Unlock Finance"}
                  {pinStep === "set" && "Create PIN"}
                  {pinStep === "confirm" && "Confirm PIN"}
                  {pinStep === "password" && "Verify Account"}
                </div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {pinStep === "enter" &&
                    "Enter your 4-6 digit PIN to continue."}
                  {pinStep === "set" && "Set a 4-6 digit PIN for quick unlock."}
                  {pinStep === "confirm" && "Re-enter the same PIN to confirm."}
                  {pinStep === "password" &&
                    "Enter account password to save your new PIN."}
                </div>
              </div>
            </div>

            {pinStep !== "password" ? (
              <form onSubmit={handleSubmit}>
                <input
                  ref={inputRef}
                  type="password"
                  inputMode="numeric"
                  pattern="\d{4,6}"
                  maxLength={6}
                  placeholder="****"
                  value={pinInput}
                  disabled={submitting}
                  onChange={(e) => handlePinInputChange(e.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-center font-mono tracking-[0.3em] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  autoFocus
                />
                {panelError && (
                  <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-700">
                    {panelError}
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      {submitting ? (
                        <span className="inline-flex items-center">
                          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/35 border-t-white" />
                        </span>
                      ) : pinStep === "enter" ? (
                        "Unlock"
                      ) : pinStep === "set" ? (
                        "Next"
                      ) : (
                        "Confirm"
                      )}
                    </button>
                    {pinStep === "enter" && enabled && (
                      <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={unlockPermanently}
                          onChange={(e) =>
                            setUnlockPermanently(e.target.checked)
                          }
                          disabled={submitting}
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        Unlock permanently
                      </label>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Numeric PIN only
                  </div>
                </div>
                {enabled && serverHasPin && (
                  <div className="mt-2 text-xs text-slate-600">
                    Lock is <b>enabled</b>. You must unlock to view this screen.
                  </div>
                )}
              </form>
            ) : (
              <form onSubmit={handleSubmit}>
                <input
                  ref={pwdRef}
                  type="password"
                  placeholder="Account password"
                  value={accountPassword}
                  onChange={(e) => setAccountPassword(e.target.value)}
                  disabled={submitting}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
                {panelError && (
                  <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-700">
                    {panelError}
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between">
                  <button
                    type="submit"
                    disabled={submitting || !accountPassword}
                    className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {submitting ? (
                      <span className="inline-flex items-center">
                        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/35 border-t-white" />
                      </span>
                    ) : (
                      "Save PIN"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPinStep("confirm");
                      setPinInput("");
                      setTimeout(() => inputRef.current?.focus(), 50);
                    }}
                    className="text-xs text-slate-600 hover:underline"
                  >
                    Back
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* tiny CSS helper for shake */}
      <style>{`
        @keyframes shake { 10%, 90% {transform: translateX(-1px);} 20%, 80% {transform: translateX(2px);} 30%, 50%, 70% {transform: translateX(-4px);} 40%, 60% {transform: translateX(4px);} }
        .animate-shake { animation: shake 0.3s; }
      `}</style>
    </div>
  );
}
